import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { BielWebSocketService } from '../../services/biel-websocket-service';
import { BielService } from '../../services/biel-service';
import { AiIconType } from '../../utils/icon-types';

@Component({
  tag: 'biel-bot',
  styleUrl: 'biel-bot.css',
  shadow: true,
})
export class BielBot {
  private TERMS_ACCEPTED_KEY = 'biel_terms_accepted';
  private CONVERSATION_KEY = 'biel_conversation_data';
  private FULLSCREEN_PREFERENCE_KEY = 'biel_fullscreen_preference';
  private THINK_MODE_PREFERENCE_KEY = 'biel_think_mode_preference';

  // Define the component internal properties
  @Prop() embeddedMode: boolean = false;
  @Prop() fetchData: boolean = true;
  @Prop({ mutable: true, reflect: true }) showModal: boolean = false;

  // Define the component properties
  @Prop() bielId: string = `biel-bot-${Math.random().toString(36).substring(2, 9)}`;
  @Prop() apiKey: string = '';
  @Prop() apiUrl: string = '';
  @Prop() disableInput: boolean = false;
  @Prop() email: string = '';
  @Prop() expandModal: boolean = false;
  @Prop({ mutable: true }) hideCloseButton: boolean = false;
  @Prop({ mutable: true }) hideExpandButton: boolean = false;
  @Prop({ mutable: true }) hideRefreshButton: boolean = false;
  @Prop({ mutable: true }) hideSettingsButton: boolean = false;
  @Prop() hideTooltips: boolean = false;
  @Prop() hideFeedback: boolean = false;
  @Prop() hideSources: boolean = false;
  @Prop() initialMessages: {
    text: string;
    sender: 'user' | 'ai';
    feedback?: 0 | 1;
    isPartial: false;
  }[] = [];
  @Prop({ mutable: true }) modalPosition: string = 'center';
  @Prop() project: string = '';
  @Prop() showTermsModal: boolean = false;
  @Prop() aiIcon: AiIconType = 'bot';
  @Prop() hideAvatars: boolean = false;
  @Prop() keepConversation: boolean = true;

  // Define texts properties
  @Prop() errorMessage403: string =
    'Oops! The request URL or API key is invalid or not authorized for this project.';
  @Prop() errorMessage404: string = 'Oops! We could not find the provided project ID.';
  @Prop() errorMessageDefault: string = 'Oops! Please try again later.';
  @Prop() footerText: string =
    'AI-generated answers may contain errors. Verify official sources before use.';
  @Prop() headerTitle: string = 'Company Name AI';
  @Prop() inputPlaceholderText: string = 'Type your message';
  @Prop() sendButtonText: string = 'Send';
  @Prop() sourcesText: string = 'Sources';
  @Prop() suggestedQuestions: string = '';
  @Prop() suggestedQuestionsTitle: string = 'Suggested questions';
  @Prop() assistantLabel: string = 'Assistant';
  @Prop() termsCheckboxText: string = 'I have read and agree to the Terms & Conditions.';
  @Prop() termsDescription: string =
    "Please review our <a href='https://biel.ai/terms' target='_blank' rel='noopener'>Terms & Conditions</a> before proceeding.";
  @Prop() termsTitle: string = 'Chatbot Terms & Conditions';
  @Prop({ mutable: true }) welcomeMessage: string;
  @Prop() refreshButtonText: string = 'Refresh';
  @Prop() expandButtonText: string = 'Expand';
  @Prop() collapseButtonText: string = 'Collapse';
  @Prop() closeButtonText: string = 'Close';
  @Prop() settingsButtonText: string = 'Settings';
  @Prop() thinkModeEnabled: boolean = false;

  // Define state properties
  @State() isThinkModeActive: boolean = false;
  @State() selectedThinkMode: 'auto' | 'fast' | 'think' = 'auto';
  @State() isFullscreen: boolean = this.expandModal;
  @State() isLoading: boolean = false;
  @State() isReceivingMessages: boolean = false;
  @State() loadedChat: boolean = false;
  @State() messages: {
    text: string;
    sender: 'user' | 'ai';
    messageId?: string;
    feedback?: 0 | 1;
    sources?: { title: string; url: string }[];
    isPartial: boolean;
    partialId?: number;
    incomplete?: boolean;
  }[] = [];
  @State() parsedSuggestedQuestions: string[] = [];
  @State() termsAccepted: boolean = false;
  @State() termsDeclined: boolean = false;
  @State() showPoweredBy: boolean = true;
  @State() conversationExpiryHours: number = 1;
  @State() showContinueButton: boolean = false;

  // Define event properties
  @Event() addMessageText: EventEmitter<{ text: string; id: number; bielId: string }>;
  @Event() closeBot: EventEmitter<void>;
  @Event() partialMessageComplete: EventEmitter<{
    partialId: number;
    bielId: string;
    msg: {
      text: string;
      sender: 'user' | 'ai';
      messageId?: string;
      feedback?: 0 | 1;
      sources?: { title: string; url: string }[];
      isPartial: boolean;
      partialId?: number;
    };
  }>;

  @Element() el: HTMLElement;

  @Listen('bielSearchButtonClick', { target: 'body' })
  handleSearchButtonClick() {
    this.handleClose(this.bielId);
  }

  @Listen('closeClicked', { target: 'body' })
  handleClose(event: CustomEvent<{ bielId: string }> | string) {
    let id: string;
    if (typeof event === 'string') {
      id = event;
    } else if (event.detail.bielId) {
      id = event.detail.bielId;
    }

    if (this.bielId === id) {
      const modal = this.el.shadowRoot.querySelector('.biel-bot-content') as HTMLElement;
      if (modal) {
        modal.style.animation = 'fadeOut 0.3s';
        setTimeout(() => {
          this.showModal = false;
          document.body.style.overflow = '';
          this.closeBot.emit();
        }, 299);
      }
    }
  }

  @Listen('fullscreenClicked', { target: 'body' })
  handleFullscreenClicked(event: CustomEvent<{ bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.isFullscreen = !this.isFullscreen;

      // Save the fullscreen preference
      this.saveFullscreenPreference(this.isFullscreen);

      setTimeout(() => {
        document.body.style.overflow = this.isFullscreen ? 'hidden' : '';
      }, 300);
    }
  }

  @Listen('suggestedQuestionClick', { target: 'body' })
  handleQuestionSelected(event: CustomEvent<{ bielId: string; message: string }>) {
    if (!this.isLoading && !this.disableInput && !this.isReceivingMessages) {
      this.showContinueButton = false;
      this.handleMessageSent(event);
    }
  }

  @Listen('feedbackSent', { target: 'body' })
  handleFeedback(event: CustomEvent<{ bielId: string; messageId: string; reaction: 0 | 1 }>) {
    if (!this.hideFeedback && this.bielId === event.detail.bielId) {
      const { messageId, reaction } = event.detail;
      this.bielService.sendFeedback(messageId, { reaction });
    }
  }

  @Listen('refreshClicked', { target: 'body' })
  handleRefreshCLicked(event: CustomEvent<{ bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.messages = [];
      this.clearStoredConversation();
      this.conectWebsocket();
      this.showContinueButton = false;
    }
  }

  @Listen('thinkModeToggled', { target: 'body' })
  handleThinkModeToggled(event: CustomEvent<{ bielId: string; isActive: boolean }>) {
    if (this.bielId === event.detail.bielId) {
      this.isThinkModeActive = event.detail.isActive;
      
      // Save the think mode preference
      this.saveThinkModePreference(this.isThinkModeActive);
      
      // Update WebSocket service if already connected
      if (this.webSocketService) {
        this.webSocketService.setThinkMode(this.isThinkModeActive);
      }
    }
  }

  @Listen('thinkModeChanged', { target: 'body' })
  handleThinkModeChanged(event: CustomEvent<{ bielId: string; mode: 'auto' | 'fast' | 'think' }>) {
    if (this.bielId === event.detail.bielId) {
      this.selectedThinkMode = event.detail.mode;
      
      // Determine if think mode should be active based on the selected mode
      // Auto and Fast = false, Think = true
      const shouldEnableThinkMode = event.detail.mode === 'think';
      this.isThinkModeActive = shouldEnableThinkMode;
      
      // Save the mode selection and preference
      this.saveThinkModeSelection(event.detail.mode);
      this.saveThinkModePreference(shouldEnableThinkMode);
      
      // Update WebSocket service if already connected
      if (this.webSocketService) {
        this.webSocketService.setThinkMode(shouldEnableThinkMode);
      }
    }
  }

  @Listen('continueClicked', { target: 'body' })
  handleContinueClicked(event: CustomEvent<{ bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.showContinueButton = false;
      
      // Find the latest user message to provide context
      const userMessages = this.messages.filter(msg => msg.sender === 'user');
      const latestUserMessage = userMessages.length > 0 ? userMessages[userMessages.length - 1].text : '';
      
      const continueMessage = latestUserMessage 
        ? `I asked "${latestUserMessage}". Please continue from where you left off.`
        : 'Please continue from where you left off.';
      
      this.messages = [
        ...this.messages,
        { text: continueMessage, sender: 'user', isPartial: false },
      ];
      if (!this.disableInput && !this.isReceivingMessages) {
        this.webSocketService.sendMessage(continueMessage);
      }
    }
  }

  @Listen('messageSent', { target: 'body' })
  handleMessageSent(event: CustomEvent<{ bielId: string; message: string }>) {
    if (event.detail.bielId === this.bielId) {
      this.messages = [
        ...this.messages,
        { text: event.detail.message, sender: 'user', isPartial: false },
      ];
      this.showContinueButton = false;
      if (!this.disableInput && !this.isReceivingMessages) {
        this.webSocketService.sendMessage(event.detail.message);
      }
    }
  }

  @Listen('termsAccepted', { target: 'body' })
  handleTermsAccepted() {
    localStorage.setItem(this.TERMS_ACCEPTED_KEY, 'true');
    this.termsAccepted = true;
  }

  @Listen('termsDeclined', { target: 'body' })
  handleTermsDeclined(event: CustomEvent<boolean>) {
    this.termsDeclined = event.detail;
  }

  modalContent: HTMLElement;
  webSocketService: BielWebSocketService;
  bielService: BielService;

  // Conversation persistence methods
  private saveConversationData(chatId: string) {
    if (!this.keepConversation || !chatId) {
      return;
    }

    const expiryTime = Date.now() + this.conversationExpiryHours * 60 * 60 * 1000;
    const conversationData = {
      projectId: this.project,
      chatId: chatId,
      expiry: expiryTime,
      url: window.location.origin,
    };

    localStorage.setItem(this.CONVERSATION_KEY, JSON.stringify(conversationData));
  }

  private getStoredConversationId(): string | null {
    if (!this.keepConversation) {
      return null;
    }

    try {
      const stored = localStorage.getItem(this.CONVERSATION_KEY);

      if (!stored) {
        return null;
      }

      const data = JSON.parse(stored);

      // Check if conversation has expired
      const now = Date.now();
      if (now > data.expiry) {
        localStorage.removeItem(this.CONVERSATION_KEY);
        return null;
      }

      // Check if it's for the same project and domain
      if (data.projectId !== this.project || data.url !== window.location.origin) {
        return null;
      }

      return data.chatId;
    } catch (error) {
      console.error('Error retrieving stored conversation:', error);
      localStorage.removeItem(this.CONVERSATION_KEY);
      return null;
    }
  }

  private clearStoredConversation() {
    localStorage.removeItem(this.CONVERSATION_KEY);
  }

  // Fullscreen preference persistence methods
  private saveFullscreenPreference(isFullscreen: boolean) {
    localStorage.setItem(this.FULLSCREEN_PREFERENCE_KEY, isFullscreen.toString());
  }

  private getStoredFullscreenPreference(): boolean | null {
    const stored = localStorage.getItem(this.FULLSCREEN_PREFERENCE_KEY);
    return stored ? stored === 'true' : null;
  }

  // Think mode preference persistence methods
  private saveThinkModePreference(isActive: boolean) {
    localStorage.setItem(this.THINK_MODE_PREFERENCE_KEY, isActive.toString());
  }

  private getStoredThinkModePreference(): boolean | null {
    const stored = localStorage.getItem(this.THINK_MODE_PREFERENCE_KEY);
    return stored ? stored === 'true' : null;
  }

  private saveThinkModeSelection(mode: 'auto' | 'fast' | 'think') {
    localStorage.setItem(this.THINK_MODE_PREFERENCE_KEY + '_selection', mode);
  }

  private getStoredThinkModeSelection(): 'auto' | 'fast' | 'think' | null {
    const stored = localStorage.getItem(this.THINK_MODE_PREFERENCE_KEY + '_selection');
    if (stored === 'auto' || stored === 'fast' || stored === 'think') {
      return stored;
    }
    return null;
  }

  constructor() {
    this.conectWebsocket();
  }

  private isMobileViewport(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 767px)').matches;
  }

  @Watch('showModal')
  handleShowModalChange(newValue: boolean) {
    if (newValue && this.isMobileViewport() && !this.embeddedMode) {
      this.isFullscreen = true;
    }
  }

  async conectWebsocket() {
    if (!this.disableInput) {
      const storedChatId = this.getStoredConversationId();

      this.webSocketService = new BielWebSocketService(
        this.project,
        this.errorMessageDefault,
        this.email,
        this.apiKey,
        this.apiUrl,
        storedChatId || '',
        this.isThinkModeActive
      );
      this.webSocketService.onMessage(this.handleIncomingMessage.bind(this));
      this.webSocketService.onLoadingStart(() => (this.isLoading = true));
      this.webSocketService.onLoadingEnd(() => (this.isLoading = false));
    }
  }

  async loadPreviousMessages() {
    if (!this.keepConversation || !this.bielService) {
      return;
    }

    const storedChatId = this.getStoredConversationId();

    if (storedChatId) {
      try {
        const previousMessages = await this.bielService.getConversationMessages(storedChatId);

        if (previousMessages.length > 0) {
          this.messages = [...previousMessages];
          this.loadedChat = true;
        }
      } catch (error) {
        if (error.message === 'FORBIDDEN_403') {
          this.clearStoredConversation();

          // Reconnect WebSocket without chatId immediately
          if (this.webSocketService) {
            this.webSocketService.closeSocket();
          }
          await this.conectWebsocket();
        } else {
          console.error('Error loading previous conversation from API:', error);
          this.clearStoredConversation();
        }
      }
    }
  }

  handleIncomingMessage(message: any) {
    const lastMessage = this.messages[this.messages.length - 1];
    const partialId = this.messages.length;

    // Save conversation ID when we receive it
    const chatIdToSave = message.chatId || this.webSocketService?.getChatId?.();

    if (chatIdToSave && this.keepConversation) {
      this.saveConversationData(chatIdToSave);
    }

    if (message.isPartial) {
      if (lastMessage?.sender !== 'ai') {
        this.messages = [
          ...this.messages,
          {
            sender: message.from,
            text: message.message,
            isPartial: message.isPartial,
            partialId: partialId + 1,
          },
        ];
      } else {
        this.addMessage(message.message, partialId);
        this.isReceivingMessages = true;
      }
    } else {
      const msg = {
        sender: message.from,
        text: message.message,
        messageId: message.messageId,
        sources: message.sources,
        isPartial: message.isPartial,
        incomplete: message.incomplete || false,
      };
      this.isReceivingMessages = false;

      // Show continue button if message is incomplete
      if (message.incomplete) {
        this.showContinueButton = true;
      }

      if (lastMessage.isPartial) {
        const bielId = this.bielId;
        this.partialMessageComplete.emit({ partialId, bielId, msg });
        this.messages[this.messages.length - 1] = msg;
      } else {
        this.messages = [...this.messages, msg];
      }
    }
  }

  addMessage(text: string, id: number) {
    setTimeout(() => {
      const bielId = this.bielId;
      this.addMessageText.emit({ text, id, bielId });
    }, 100);
  }

  async componentWillLoad() {
    // Load stored fullscreen preference
    const storedFullscreenPreference = this.getStoredFullscreenPreference();
    if (storedFullscreenPreference !== null) {
      this.isFullscreen = storedFullscreenPreference;
    }

    // Load stored think mode selection
    const storedThinkModeSelection = this.getStoredThinkModeSelection();
    if (storedThinkModeSelection) {
      this.selectedThinkMode = storedThinkModeSelection;
      this.isThinkModeActive = storedThinkModeSelection === 'think';
    } else {
      // Fallback to old preference format
      const storedThinkModePreference = this.getStoredThinkModePreference();
      if (storedThinkModePreference !== null) {
        this.isThinkModeActive = storedThinkModePreference;
        this.selectedThinkMode = storedThinkModePreference ? 'think' : 'auto';
      } else if (this.thinkModeEnabled) {
        this.isThinkModeActive = true;
        this.selectedThinkMode = 'think';
      }
    }

    if (this.embeddedMode) {
      this.showModal = true;
      this.hideCloseButton = true;
      this.hideExpandButton = true;
      this.hideRefreshButton = true;
      this.hideSettingsButton = true;
      this.modalPosition = 'default';
      this.isFullscreen = false;
    }

    if (this.initialMessages.length > 0) {
      this.messages = [...this.initialMessages];
      this.loadedChat = true;
    }

    if (this.fetchData && !this.disableInput) {
      this.bielService = new BielService(
        this.project,
        this.errorMessage404,
        this.errorMessage403,
        this.errorMessageDefault,
        this.apiKey,
        this.apiUrl
      );

      if (this.suggestedQuestions) {
        try {
          this.parsedSuggestedQuestions = JSON.parse(this.suggestedQuestions);
        } catch (e) {
          // If parsing fails, split by comma and trim whitespace
          this.parsedSuggestedQuestions = this.suggestedQuestions.split(',').map((q) => q.trim());
        }
      }

      const result = await this.bielService.getProjectDetails();

      if (typeof result === 'string') {
        this.welcomeMessage = this.welcomeMessage || result;
      } else {
        this.showPoweredBy = !result.whitelabel;
        if (!this.parsedSuggestedQuestions?.length) {
          this.parsedSuggestedQuestions = result.suggestedQuestions;
        }
        if (!this.welcomeMessage) {
          this.welcomeMessage = result.initialMessage;
        }
        // Hide think mode button if project doesn't support it
        if (!result.think_mode) {
          this.hideSettingsButton = true;
        } else if (this.embeddedMode) {
          // Show think mode button in embedded mode if project supports it
          this.hideSettingsButton = false;
        }
      }
    }

    if (this.showTermsModal) {
      this.termsAccepted = localStorage.getItem(this.TERMS_ACCEPTED_KEY) === 'true';
    }

    // Load previous messages after bielService is initialized
    // This will also validate the chatId and reconnect WebSocket if necessary
    await this.loadPreviousMessages();
  }

  render() {
    return (
      <div class={`biel-bot-wrapper ${this.embeddedMode ? 'biel-bot-wrapper--embedded' : ''}`}>
        {this.showModal && this.showTermsModal && !this.termsAccepted && !this.termsDeclined && (
          <biel-terms-modal
            termsTitle={this.termsTitle}
            termsDescription={this.termsDescription}
            termsCheckboxText={this.termsCheckboxText}
          />
        )}
        {this.showModal && (this.termsAccepted || !this.showTermsModal) && (
          <div class="feedback-overlay"></div>
        )}
        {this.showModal && (this.termsAccepted || !this.showTermsModal) && (
          <div
            class={`biel-bot-content biel-bot-content--${this.modalPosition} ${this.showModal ? 'biel-bot-content--open' : ''} ${this.isFullscreen ? 'fullscreen' : ''}`}
            ref={(el) => (this.modalContent = el)}
          >
            <div class="biel-bot-header">
              <biel-header
                headerTitle={this.headerTitle}
                project={this.project}
                showPoweredBy={this.showPoweredBy}
                hideExpandButton={this.hideExpandButton}
                hideCloseButton={this.hideCloseButton}
                hideRefreshButton={this.hideRefreshButton}
                hideSettingsButton={this.hideSettingsButton}
                hideTooltips={this.hideTooltips}
                fullscreen={this.isFullscreen}
                bielId={this.bielId}
                thinkModeEnabled={this.isThinkModeActive}
                currentThinkMode={this.selectedThinkMode}
                refreshButtonText={this.refreshButtonText}
                expandButtonText={this.expandButtonText}
                collapseButtonText={this.collapseButtonText}
                closeButtonText={this.closeButtonText}
                settingsButtonText={this.settingsButtonText}
              ></biel-header>
            </div>
            <div class="biel-bot-body">
              <biel-chat
                messages={this.messages}
                welcomeMessage={this.welcomeMessage}
                hideFeedback={this.hideFeedback}
                loadedChat={this.loadedChat}
                suggestedQuestions={this.parsedSuggestedQuestions}
                suggestedQuestionsTitle={this.suggestedQuestionsTitle}
                isFullscreen={this.isFullscreen}
                isLoading={this.isLoading}
                embeddedMode={this.embeddedMode}
                modalPosition={this.modalPosition}
                bielId={this.bielId}
                sourcesText={this.sourcesText}
                aiIcon={this.aiIcon}
                hideAvatars={this.hideAvatars}
                hideSources={this.hideSources}
                showContinueButton={this.showContinueButton}
                assistantLabel={this.assistantLabel}
              ></biel-chat>
              <biel-input
                placeholderText={this.inputPlaceholderText}
                sendButtonText={this.sendButtonText}
                isDisabled={this.isLoading || this.disableInput || this.isReceivingMessages}
                bielId={this.bielId}
              ></biel-input>
            </div>
            <div class="biel-bot-footer" innerHTML={this.footerText}></div>
          </div>
        )}
      </div>
    );
  }
}
